/**
 * utils/time.ts — Funções de formatação de tempo
 * Centralizado de AgentesPage, Integrations, ChatHubPage
 */

/**
 * Retorna tempo decorrido em formato curto (1s, 5min, 2h, 3d)
 * Usado em: AgentesPage, Integrations, ChatHubPage
 */
export function timeAgo(dateStr: string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  
  // Menos de 1 minuto
  if (diff < 60_000) {
    const secs = Math.round(diff / 1000);
    return secs < 1 ? 'agora' : `${secs}s`;
  }
  
  // Menos de 1 hora
  if (diff < 3600_000) {
    return `${Math.round(diff / 60000)}min`;
  }
  
  // Menos de 1 dia
  if (diff < 86400_000) {
    return `${Math.round(diff / 3600000)}h`;
  }
  
  // Dias
  return `${Math.floor(diff / 86400000)}d`;
}

/**
 * Retorna tempo decorrido em formato longo (agora, 5min atras, 2h atras, 3d atras)
 * Usado em: Integrations
 */
export function timeAgoLong(iso: string): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  
  if (mins < 1) return 'agora';
  if (mins < 60) return `${mins}min atras`;
  
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h atras`;
  
  const days = Math.floor(hours / 24);
  return `${days}d atras`;
}

/**
 * Formata timestamp em horário (HH:MM)
 * Usado em: ChatHubPage
 */
export function formatTime(ts?: string): string {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Formata timestamp em data relativa (Hoje, Ontem, DD/MM)
 * Usado em: ChatHubPage
 */
export function formatDate(ts?: string): string {
  if (!ts) return '';
  const d = new Date(ts);
  const today = new Date();
  
  if (d.toDateString() === today.toDateString()) return 'Hoje';
  
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Ontem';
  
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/**
 * Alias de timeAgo para compatibilidade com ChatHubPage
 */
export function formatTimeAgo(ts: string): string {
  return timeAgo(ts);
}
